import { useNavigate } from 'react-router-dom';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import React, { useState, useEffect, useCallback } from 'react';

import ApplicationsList from './components/ApplicationsList';
import KanbanBoard from './components/KanbanBoard';
import BulkRefreshBanner from './components/BulkRefreshBanner';
import MonitoringHeader from './components/MonitoringHeader';
import SearchAndFilters from './components/SearchAndFilters';
import StatCards from './components/StatCards';

import { DEFAULT_FILTERS } from './constants';
import { useApplicationsList } from './hooks/useApplicationsList';
import { useBulkRefresh } from './hooks/useBulkRefresh';
import { TabType, LearningGlobalSummary } from './types';
import { fetchGlobalSummary } from './api';

interface ApplicationMonitoringPageProps {
  onLogout?: () => void;
  username?: string;
  role?: string;
}

const ApplicationMonitoringPage: React.FC<ApplicationMonitoringPageProps> = () => {
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    return (sessionStorage.getItem('monitoringActiveTab') as TabType) || 'list';
  });

  const [globalSummary, setGlobalSummary] = useState<LearningGlobalSummary | null>(null);

  const {
    items,
    totalItems,
    totalPages,
    page,
    loading,
    error,
    cachedAt,
    searchQuery,
    searchType,
    setSearchType,
    filters,
    appliedFilters,
    setPage,
    setSearchQuery,
    setFilters,
    applyFilters,
    resetFilters,
    reload,
  } = useApplicationsList(activeTab);

  const loadGlobalSummary = useCallback(async () => {
    try {
      const summary = await fetchGlobalSummary();
      setGlobalSummary(summary);
    } catch (err) {
      console.error('Ошибка при загрузке глобальной статистики:', err);
    }
  }, []);

  useEffect(() => {
    loadGlobalSummary();
  }, [loadGlobalSummary]);

  useEffect(() => {
    if (!loading && items.length > 0) {
      const savedScroll = sessionStorage.getItem('monitoringScrollPosition');
      if (savedScroll) {
        setTimeout(() => {
          window.scrollTo({
            top: parseInt(savedScroll, 10),
            left: 0,
            behavior: 'auto'
          });
          sessionStorage.removeItem('monitoringScrollPosition');
        }, 0);
      }
    }
  }, [loading, items]);

  const handleFullReload = useCallback(() => {
    reload();
    loadGlobalSummary();
  }, [reload, loadGlobalSummary]);

  const { isRefreshing, bulkStatus, triggerRefresh } = useBulkRefresh(handleFullReload);

  const navigate = useNavigate();

  const hasActiveFilters =
    appliedFilters.overall_status !== DEFAULT_FILTERS.overall_status ||
    appliedFilters.nearest_deadline_from !== DEFAULT_FILTERS.nearest_deadline_from ||
    appliedFilters.nearest_deadline_to !== DEFAULT_FILTERS.nearest_deadline_to ||
    appliedFilters.sort_by !== DEFAULT_FILTERS.sort_by ||
    appliedFilters.department_name !== DEFAULT_FILTERS.department_name ||
    appliedFilters.specialist_query !== DEFAULT_FILTERS.specialist_query ||
    appliedFilters.student_query !== DEFAULT_FILTERS.student_query;

  const handleDetails = (id: number) => {
    sessionStorage.setItem('monitoringScrollPosition', window.scrollY.toString());
    const selectedItem = items.find((item) => item.application_id === id);
    navigate(`/monitoring/${id}`, { state: { summary: selectedItem } });
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    sessionStorage.setItem('monitoringActiveTab', tab);
  };

  return (
    <div className="w-full">
      <div className="p-4 lg:p-8 space-y-6">

        <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-3">
          <AssessmentOutlinedIcon fontSize="large" className="text-blue-600" />
          Мониторинг
        </h1>

        <MonitoringHeader
          activeTab={activeTab}
          onTabChange={handleTabChange}
          cachedAt={cachedAt}
          isRefreshing={isRefreshing}
          onRefresh={triggerRefresh}
        />

        <BulkRefreshBanner status={bulkStatus} />

        <StatCards summary={globalSummary} />

        <SearchAndFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchType={searchType}
          onSearchTypeChange={setSearchType}
          filters={filters}
          appliedFilters={appliedFilters}
          onFiltersChange={setFilters}
          onApply={applyFilters}
          onReset={resetFilters}
          hasActiveFilters={hasActiveFilters}
        />

        {activeTab === 'list' ? (
          <ApplicationsList
            items={items}
            loading={loading}
            error={error}
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={setPage}
            onDetails={handleDetails}
            onRetry={handleFullReload}
          />
        ) : (
          <KanbanBoard
            items={items}
            loading={loading}
            onDetails={handleDetails}
          />
        )}

      </div>
    </div>
  );
};

export default ApplicationMonitoringPage;